/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import Steps from "../../components/elements/steps";
import { Stepx } from "../../utils/data";
import mtn from "../../assets/images/mtn.png";
import { useAppStore } from "../../store";
import PhoneNumberInput from "../../components/form/phone-number";
import { Button, Select, Spin, notification, Input, Space } from "antd";
import { TagOutlined } from "@ant-design/icons";
import Colors from "../../constants/Colors";
import { useNavigate } from "react-router-dom";
import { convertToInternationalFormat, getCouponErrorMessage } from "../../utils";
import DataProvider from "../../services";
import images from "../../assets/images/poweredbymtn.png";
import axios from "axios";

export default function Billing() {
  const [api, contextHolder] = notification.useNotification();
  const { plan, user } = useAppStore();
  const navigate = useNavigate();
  const [number, setNumber] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [couponInput, setCouponInput] = useState<string>("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<"Monthly" | "Yearly">(
    "Monthly"
  );
  const [selectedPrice, setSelectedPrice] = useState(plan?.price || 0);

  const handleApplyCoupon = async () => {
    const code = couponInput?.trim().toUpperCase();
    if (!code) {
      api.warning({ message: "Coupon", description: "Enter a coupon code." });
      return;
    }
    setValidatingCoupon(true);
    try {
      const result = await DataProvider.validateCoupon(code);
      if (result.valid) {
        setAppliedCoupon(code);
        api.success({
          message: "Coupon applied",
          description: `"${code}" will be applied when you proceed.`,
        });
      } else {
        api.error({
          message: "Invalid coupon",
          description: result.message ?? "This coupon is not valid.",
        });
      }
    } catch {
      api.error({
        message: "Invalid coupon",
        description:
          "Could not validate coupon. Please check the code and try again.",
      });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  const sortedOptions = [
    { value: "Monthly", label: "1 month" },
    { value: "Yearly", label: "1 year" },
  ];

  const handlePeriodChange = (value: any) => {
    if (value === "Monthly" || value === "Yearly") {
      setSelectedPeriod(value);
      const price = plan?.price || 0;
      const multiplier = value === "Monthly" ? 1 : 12; // Yearly means 12 months
      const totalPrice = price * multiplier;
      setSelectedPrice(totalPrice);
    }
  };

  useEffect(() => {
    if (!user) navigate("/sign-up");
  }, [user, navigate]);

  async function onSubmit() {
    if (!number || number.length > 10 || number.length < 10) {
      return api["warning"]({
        message: "Warning",
        description: "Phone number is required.",
      });
    }

    if (!number.startsWith("0")) {
      return api["warning"]({
        message: "Warning",
        description: "Phone number must start with 0.",
      });
    }

    if (!selectedPeriod) {
      return api["warning"]({
        message: "Warning",
        description: "Subscription duration is required.",
      });
    }

    if (plan?.id == null) {
      return api["error"]({
        message: "Error",
        description: "Plan not selected. Please go back and choose a plan.",
      });
    }

    const payload: {
      mcareId: number;
      msisdn: string;
      frequencyType: string;
      couponCode?: string;
    } = {
      mcareId: plan.id,
      msisdn: convertToInternationalFormat(number),
      frequencyType: selectedPeriod,
    };
    if (appliedCoupon) payload.couponCode = appliedCoupon;

    try {
      setLoading(true);
      const { data } = await DataProvider.requestSubscription(payload);
      const isCouponSuccess =
        data?.statusMessage &&
        String(data.statusMessage).toLowerCase().includes("coupon");
      if (isCouponSuccess) {
        setLoading(false);
        navigate("/plan/comfirm-pay-00725?success=coupon");
        return;
      }
      const tracking = localStorage.getItem("@tracking");
      if (tracking && data?.id) {
        const payload = await JSON.parse(tracking);
        const load = await DataProvider.updateTracking({
          source: payload.source,
          campaign: payload.campaign,
          trackingId: payload.trackingId,
          subscriptionId: data.id,
        });
        if (load.data) {
          localStorage.setItem("@tracking", JSON.stringify(load.data));
        }
      }
      setLoading(false);
      navigate("/plan/comfirm-pay-00725");
    } catch (error: any) {
      setLoading(false);
      if (axios.isAxiosError(error)) {
        if (
          error.code &&
          (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK")
        ) {
          return api["error"]({
            message: "Internet connection failure",
            description: "Kindly check your internet and try again.",
          });
        }
        if (appliedCoupon) {
          return api["error"]({
            message: "Coupon",
            description: getCouponErrorMessage(error),
          });
        }
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          const errorMessage = error.response.data.message;
          return api["error"]({
            message: "Failed",
            description:
              errorMessage === "Offer does not exist"
                ? "The subscription offer selected does not exist."
                : errorMessage === "Duplicate Request"
                ? "Too many requests, kindly try again 10 minutes later."
                : errorMessage,
          });
        } else {
          return api["error"]({
            message: "Failed",
            description:
              error.message === "Offer does not exist"
                ? "The subscription offer selected does not exist."
                : error.message === "Duplicate Request"
                ? "Too many requests, kindly try again 10 minutes later."
                : error.message,
          });
        }
      } else {
        return api["error"]({
          message: "Failed",
          description: "Subscription payment failed, try again.",
        });
      }
    }
  }

  return (
    <div className="min-h-screen w-full flex flex-col justify-start items-center py-12">
      <div className="mt-4 mb-5 lg:w-[60%] md:w-[80%] w-full flex flex-col justify-center items-center">
        <Steps current={2} items={Stepx} />
        <div className="grid md:grid-cols-2 grid-cols-1 gap-5 w-full my-5 p-4">
          <div className="border border-solid border-gray-200 p-4">
            <div className="bg-black w-full h-20 flex justify-center mb-4 items-center">
              <h1 className="text-white text-2xl font-semibold">
                {plan?.description}
              </h1>
            </div>
            <div className="py-2 border-b flex justify-between items-center">
              <p className="md:text-base text-sm">Mcare {plan?.description}</p>
              <p className="md:text-base text-sm">GH¢{plan?.price}/mo</p>
            </div>
            <div className="py-2 border-b flex justify-between items-center">
              <p className="md:text-base text-sm">Period</p>
              <p className="md:text-base text-sm">{selectedPeriod}</p>
            </div>
            {appliedCoupon && (
              <div className="py-2 border-b flex justify-between items-center">
                <p className="md:text-base text-sm">Coupon</p>
                <p className="md:text-base text-sm font-medium text-green-600">
                  {appliedCoupon}
                </p>
              </div>
            )}
            <div className="pt-2 flex justify-between items-center">
              <p className="md:text-base text-sm">Total</p>
              {/* <p className="md:text-base text-sm">GH¢{plan?.price}</p> */}
              <p className="md:text-base text-sm">GH¢{selectedPrice}</p>
            </div>
            <div className="flex justify-center mt-7">
              <img
                src={images}
                alt="care"
                className="md:h-20 h-10 rounded-l-lg"
              />
            </div>
          </div>
          <div className="border border-solid border-gray-200 p-4">
            <div className="bg-gray-100 w-full h-16 flex justify-center items-center">
              <h3 className="font-medium text-base">Mobile Money</h3>
            </div>
            <div className="">
              <div className="mt-5">
                <h3 className="font-medium text-base">Subscription Duration</h3>
              </div>
              <div className="">
                <Select
                  style={{ marginTop: 7, border: 1, outline: "none" }}
                  placeholder="Select duration"
                  optionFilterProp="label"
                  options={sortedOptions}
                  className="w-full h-12"
                  onChange={handlePeriodChange}
                />
              </div>

              <PhoneNumberInput
                required
                title="Enter Phone Numbers"
                value={number}
                onChangeText={setNumber}
                placeholder="eg:0240000000"
              />

              <div className="mb-4">
                <label
                  htmlFor="billing-coupon-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <TagOutlined className="mr-1" aria-hidden />
                  Have a coupon?
                </label>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                    <span className="text-sm font-medium text-green-800">
                      {appliedCoupon}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      onClick={handleRemoveCoupon}
                      className="text-gray-600 p-0 h-auto"
                      aria-label="Remove coupon"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Space.Compact className="w-full">
                    <Input
                      id="billing-coupon-input"
                      placeholder="Enter coupon code"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onPressEnter={handleApplyCoupon}
                      className="rounded-l-lg rounded-r-none"
                      maxLength={64}
                      aria-label="Coupon code"
                    />
                    <Button
                      type="default"
                      onClick={handleApplyCoupon}
                      loading={validatingCoupon}
                      className="rounded-r-lg rounded-l-none"
                      aria-label="Apply coupon"
                    >
                      Apply
                    </Button>
                  </Space.Compact>
                )}
              </div>

              <div>
                <select
                  title="network"
                  name="network"
                  id="billing-network-select"
                  value="mtn"
                  onChange={() => {}}
                  className="rounded-lg bg-white px-2 outline-none border border-solid border-gray-200 w-full h-12 text-base"
                  aria-label="Select network"
                >
                  <option value="mtn" className="flex items-center">
                    <img src={mtn} alt="mtn" className="h-7" />
                    MTN
                  </option>
                </select>
                <p className="text-gray-500 md:text-sm text-xs my-2">
                  By Clicking “Proceed with payment” you agree to the terms of
                  service of the company
                </p>
              </div>
              <div className="w-full mt-4">
                <Button
                  onClick={onSubmit}
                  size="large"
                  style={{ backgroundColor: Colors.primary, color: "white" }}
                  className="w-full"
                >
                  Proceed with payment
                </Button>
                <Button
                  onClick={() => navigate("/pick-plan")}
                  size="large"
                  className="w-full mt-4"
                >
                  Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Spin spinning={loading} fullscreen />
      {contextHolder}
    </div>
  );
}
